import { Switch } from '@react-spectrum/s2';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi';
import { isSquareSandboxMode, setSquareSandboxMode } from '@/api/SquareApi';
import { useImsApiContext } from '@/auth/ImsApiContext.tsx';
import {
	isRegionBlockForced,
	setRegionBlockForced,
} from '@/auth/regionBlockOverride';
import { useUserSettings } from '@/hooks/useUserSettings';
import { describeDelivery } from '@/spaces/chat/panels/smsDelivery';

type RunStatus =
	| { kind: 'idle' }
	| { kind: 'running' }
	| { kind: 'success'; threadId: string | null; agentId: string }
	| { kind: 'error'; message: string };

type SmsRunStatus =
	| { kind: 'idle' }
	| { kind: 'running'; threadId?: string; agentId?: string }
	| {
			kind: 'sent';
			to: string;
			sentAt: string;
			threadId: string;
			deliveryLabel: string;
	  }
	| { kind: 'skipped' }
	| { kind: 'error'; message: string; threadId?: string };

type SimulateStatus =
	| { kind: 'idle' }
	| { kind: 'running' }
	| { kind: 'ok'; commentId: string; watchersMatched: number }
	| { kind: 'no_watchers' }
	| { kind: 'error'; message: string };

type ActivateAgentStatus =
	| { kind: 'idle' }
	| { kind: 'running' }
	| { kind: 'success'; agentId: string; threadId: string | null }
	| { kind: 'error'; message: string };

// Every daily-SMS ledger status that proves the SMS provider took the text.
// "sent" is the legacy spelling of "accepted" and still sits on older rows.
const SENT_LEDGER_STATUSES = new Set([
	'sent',
	'accepted',
	'delivered',
	'undelivered',
]);

type StrategyListItem = {
	id: string;
	status: string;
	wish?: string;
	creativity_level?: number | null;
	started_at: string;
};

export const TestingUtils = () => {
	const userId = useImsApiContext()?.userId ?? null;
	const bridgewayApi = useBridgewayApi();
	const bridgeApi = useBridgeApi();
	const { settings, putSettings } = useUserSettings();
	const [status, setStatus] = useState<RunStatus>({ kind: 'idle' });
	const [smsStatus, setSmsStatus] = useState<SmsRunStatus>({ kind: 'idle' });
	const [simulateStatus, setSimulateStatus] = useState<SimulateStatus>({
		kind: 'idle',
	});
	const [simulateText, setSimulateText] = useState(
		'Love this! How much is it?',
	);
	const [sandboxMode, setSandboxMode] = useState(() => isSquareSandboxMode());
	const [forceRegionBlock, setForceRegionBlock] = useState(() =>
		isRegionBlockForced(),
	);
	const smsPollCount = useRef(0);
	const [strategies, setStrategies] = useState<StrategyListItem[]>([]);
	const [selectedStrategyId, setSelectedStrategyId] = useState('');
	const [activateAgentStatus, setActivateAgentStatus] =
		useState<ActivateAgentStatus>({ kind: 'idle' });

	const phoneNumber = settings?.phone_number ?? null;

	const fetchStrategies = useCallback(async () => {
		if (!userId) return;
		try {
			const all = await bridgewayApi.get<StrategyListItem[]>(
				`/api/user/${userId}/strategies/`,
			);
			setStrategies(all.filter((s) => s.status === 'complete'));
		} catch {
			// silent — dropdown will just be empty
		}
	}, [userId, bridgewayApi]);

	useEffect(() => {
		void fetchStrategies();
	}, [fetchStrategies]);

	const onActivateStrategyAgent = async () => {
		if (!userId || !selectedStrategyId) return;
		setActivateAgentStatus({ kind: 'running' });
		try {
			const res = await bridgewayApi.post<{
				agent_id: string;
				agent_name: string;
				status: string;
			}>(`/api/user/${userId}/agents/activate-strategy`, {
				strategy_id: selectedStrategyId,
			});
			const { threadId, agentId } = await runAgentByName(res.agent_name);
			setActivateAgentStatus({
				kind: 'success',
				agentId,
				threadId,
			});
			void fetchStrategies();
		} catch (err) {
			setActivateAgentStatus({
				kind: 'error',
				message: err instanceof Error ? err.message : String(err),
			});
		}
	};

	const runAgentByName = async (
		name: string,
	): Promise<{ threadId: string | null; agentId: string }> => {
		if (!userId) throw new Error('No user id');
		const agents = await bridgewayApi.get<Array<{ id: string; name: string }>>(
			`/api/user/${userId}/agents/`,
		);
		const match = agents.find((a) => a.name === name);
		if (!match) {
			throw new Error(
				`No ${name} agent found for this user. Has onboarding completed?`,
			);
		}
		const runRes = await bridgewayApi.post<{
			thread_id?: string;
			agent_id?: string;
			status?: string;
		}>(`/api/user/${userId}/agents/${match.id}/run`, {
			task_input: 'Scheduled heartbeat cycle.',
		});
		return { threadId: runRes.thread_id ?? null, agentId: match.id };
	};

	const onClick = async () => {
		if (!userId) return;
		setStatus({ kind: 'running' });
		try {
			const { threadId, agentId } = await runAgentByName('insights_curator');
			setStatus({ kind: 'success', threadId, agentId });
		} catch (err) {
			setStatus({
				kind: 'error',
				message: err instanceof Error ? err.message : String(err),
			});
		}
	};

	const onRunDailySms = async () => {
		if (!userId || !phoneNumber) return;
		if (
			!confirm(
				`Send today's daily SMS to ${phoneNumber}? This runs the real production pipeline.`,
			)
		) {
			return;
		}
		setSmsStatus({ kind: 'running' });
		try {
			const { threadId, agentId } = await runAgentByName('daily_sms');
			if (threadId && agentId) {
				smsPollCount.current = 0;
				setSmsStatus({ kind: 'running', threadId, agentId });
			} else {
				setSmsStatus({ kind: 'error', message: 'No thread_id returned.' });
			}
		} catch (err) {
			setSmsStatus({
				kind: 'error',
				message: err instanceof Error ? err.message : String(err),
			});
		}
	};

	const pollSmsRun = useCallback(async () => {
		if (smsStatus.kind !== 'running' || !smsStatus.threadId || !userId) return;
		const { threadId, agentId } = smsStatus;
		smsPollCount.current += 1;
		if (smsPollCount.current > 15) {
			setSmsStatus({ kind: 'error', message: 'Timed out waiting for agent.' });
			return;
		}
		try {
			const detail = await bridgewayApi.get<{
				status: string;
				error: string | null;
			}>(`/api/user/${userId}/agents/${agentId}/runs/${threadId}`);

			if (detail.status === 'pending' || detail.status === 'running') return;
			if (detail.status === 'error' || detail.error) {
				setSmsStatus({
					kind: 'error',
					message: detail.error ?? 'Agent run failed.',
				});
				return;
			}

			const history = await bridgewayApi.get<
				Array<{
					status: string;
					to?: string;
					sent_at?: string;
					delivered_at?: string;
					delivery_detail?: string;
				}>
			>(`/api/user/${userId}/daily_sms/history?limit=1`);

			const recent = history[0];
			const isRecent =
				recent?.sent_at &&
				Date.now() - new Date(recent.sent_at).getTime() < 120_000;

			// This tile answers "did a text go out", not "did the handset get it".
			// Every one of these statuses means the SMS provider took the text;
			// they differ only on what the carrier reported afterwards.
			if (SENT_LEDGER_STATUSES.has(recent?.status ?? '') && isRecent) {
				setSmsStatus({
					kind: 'sent',
					to: recent.to ?? phoneNumber ?? '',
					sentAt: recent.sent_at!,
					threadId: threadId!,
					deliveryLabel: describeDelivery(recent),
				});
			} else {
				setSmsStatus({ kind: 'skipped' });
			}
		} catch (err) {
			setSmsStatus({
				kind: 'error',
				message: err instanceof Error ? err.message : String(err),
				threadId: threadId,
			});
		}
	}, [smsStatus, userId, bridgewayApi, phoneNumber]);

	useEffect(() => {
		if (smsStatus.kind !== 'running' || !smsStatus.threadId) return;
		const id = setInterval(() => void pollSmsRun(), 5000);
		void pollSmsRun();
		return () => clearInterval(id);
		// `smsStatus.threadId` cannot be named here — it exists on only three of the
		// five SmsRunStatus variants, so referencing it unnarrowed is a type error.
		// It is already covered transitively: pollSmsRun depends on the whole
		// smsStatus object, so a threadId change produces a new callback identity
		// and re-runs this effect.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [smsStatus.kind, pollSmsRun]);

	const onSimulateComment = async () => {
		if (!userId) return;
		setSimulateStatus({ kind: 'running' });
		try {
			const res = await bridgewayApi.post<{
				status: string;
				comment_id?: string;
				watchers_matched?: number;
			}>(`/api/user/${userId}/instagram/dev/simulate-comment`, {
				comment_text: simulateText,
			});
			if (res.status === 'ok') {
				setSimulateStatus({
					kind: 'ok',
					commentId: res.comment_id ?? '',
					watchersMatched: res.watchers_matched ?? 0,
				});
			} else {
				setSimulateStatus({ kind: 'no_watchers' });
			}
		} catch (err) {
			setSimulateStatus({
				kind: 'error',
				message: err instanceof Error ? err.message : String(err),
			});
		}
	};

	const handleRegionBlockToggle = (isSelected: boolean) => {
		setRegionBlockForced(isSelected);
		setForceRegionBlock(isSelected);
	};

	// The override is one-shot: it self-consumes on the next login, which may
	// happen in another tab or while this page is backgrounded. Re-read the
	// flag whenever this view regains focus/visibility (and on cross-tab
	// storage writes) so the Switch never shows "on" for an already-cleared
	// flag.
	useEffect(() => {
		const syncFromStorage = () => setForceRegionBlock(isRegionBlockForced());
		window.addEventListener('focus', syncFromStorage);
		document.addEventListener('visibilitychange', syncFromStorage);
		window.addEventListener('storage', syncFromStorage);
		return () => {
			window.removeEventListener('focus', syncFromStorage);
			document.removeEventListener('visibilitychange', syncFromStorage);
			window.removeEventListener('storage', syncFromStorage);
		};
	}, []);

	const handleSandboxToggle = (isSelected: boolean) => {
		setSquareSandboxMode(isSelected);
		setSandboxMode(isSelected);
		void (async () => {
			const result = await putSettings({ square_connection: null });
			if (!result) {
				setSandboxMode(!isSelected);
				setSquareSandboxMode(!isSelected);
			}
		})();
	};

	const isRunning = status.kind === 'running';
	const isSmsRunning = smsStatus.kind === 'running';
	const isSimulating = simulateStatus.kind === 'running';
	const isActivatingAgent = activateAgentStatus.kind === 'running';

	return (
		<div
			className="flex flex-1 flex-col overflow-hidden rounded-lg bg-white
				shadow"
		>
			<div className="border-b border-gray-200 bg-gray-50 p-4">
				<h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
			</div>

			<div className="flex-1 space-y-6 overflow-y-auto p-6">
				<section className="space-y-3">
					<h3 className="text-sm font-semibold text-gray-700">Insights</h3>
					<button
						type="button"
						disabled={!userId || isRunning}
						onClick={() => void onClick()}
						className="w-full rounded bg-blue-600 px-4 py-2 text-sm text-white
							transition hover:bg-blue-700 disabled:cursor-not-allowed
							disabled:opacity-50"
					>
						{isRunning
							? 'Running insights curator...'
							: 'Run Insights Curator (daily cycle)'}
					</button>

					{status.kind === 'success' && (
						<div
							className="rounded border border-green-200 bg-green-50 px-3 py-2
								text-xs text-green-800"
						>
							<div className="font-semibold">Dispatched successfully</div>
							<div className="mt-1 break-all">agent_id: {status.agentId}</div>
							{status.threadId && (
								<div className="break-all">thread_id: {status.threadId}</div>
							)}
							<div className="mt-1 text-green-700">
								Curator is running async. New rows in curated_insights typically
								appear within ~30s.
							</div>
						</div>
					)}

					{status.kind === 'error' && (
						<div
							className="rounded border border-red-200 bg-red-50 px-3 py-2
								text-xs text-red-800"
						>
							<div className="font-semibold">Failed</div>
							<div className="mt-1 wrap-break-word">{status.message}</div>
						</div>
					)}
				</section>

				<section className="space-y-3 border-t border-gray-200 pt-4">
					<h3 className="text-sm font-semibold text-gray-700">
						Strategy Agent
					</h3>
					<div className="text-xs text-gray-500">
						Activate a daily-loop agent for a completed strategy. Registers a
						heartbeat and kicks off the first run immediately.
					</div>
					<select
						value={selectedStrategyId}
						onChange={(e) => setSelectedStrategyId(e.target.value)}
						className="w-full rounded border border-gray-300 px-3 py-2 text-sm
							text-gray-900"
					>
						<option value="">
							{strategies.length
								? 'Select a completed strategy...'
								: 'No completed strategies'}
						</option>
						{strategies.map((s) => (
							<option key={s.id} value={s.id}>
								{s.wish ?? 'Untitled'} (lvl {s.creativity_level ?? '?'}) —{' '}
								{s.id.slice(0, 8)}
							</option>
						))}
					</select>
					<button
						type="button"
						disabled={!userId || !selectedStrategyId || isActivatingAgent}
						onClick={() => void onActivateStrategyAgent()}
						className="w-full rounded bg-purple-600 px-4 py-2 text-sm text-white
							transition hover:bg-purple-700 disabled:cursor-not-allowed
							disabled:opacity-50"
					>
						{isActivatingAgent ? 'Activating...' : 'Activate Strategy Agent'}
					</button>

					{activateAgentStatus.kind === 'success' && (
						<div
							className="rounded border border-green-200 bg-green-50 px-3 py-2
								text-xs text-green-800"
						>
							<div className="font-semibold">Strategy agent activated</div>
							<div className="mt-1 break-all">
								agent_id: {activateAgentStatus.agentId}
							</div>
							{activateAgentStatus.threadId && (
								<div className="break-all">
									thread_id: {activateAgentStatus.threadId}
								</div>
							)}
							<div className="mt-1 text-green-700">
								Daily heartbeat registered. First run dispatched.
							</div>
						</div>
					)}

					{activateAgentStatus.kind === 'error' && (
						<div
							className="rounded border border-red-200 bg-red-50 px-3 py-2
								text-xs text-red-800"
						>
							<div className="font-semibold">Activation failed</div>
							<div className="mt-1 wrap-break-word">
								{activateAgentStatus.message}
							</div>
						</div>
					)}
				</section>

				<section className="space-y-3 border-t border-gray-200 pt-4">
					<h3 className="text-sm font-semibold text-gray-700">Daily SMS</h3>
					<div className="text-xs text-gray-500">
						{phoneNumber ? (
							<>
								Will send to: <span className="font-mono">{phoneNumber}</span>
							</>
						) : (
							'No phone number on file — set one in Settings.'
						)}
					</div>
					<button
						type="button"
						disabled={!userId || !phoneNumber || isSmsRunning}
						onClick={() => void onRunDailySms()}
						className="w-full rounded bg-blue-600 px-4 py-2 text-sm text-white
							transition hover:bg-blue-700 disabled:cursor-not-allowed
							disabled:opacity-50"
					>
						{smsStatus.kind === 'running' ? 'Running...' : 'Send Daily SMS now'}
					</button>

					<div
						className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2
							text-xs text-yellow-800"
					>
						Only one SMS per local day. Already sent today? Use{' '}
						<span className="font-semibold">
							Clear Today&apos;s Daily SMS Send Record
						</span>{' '}
						in Cleanup below to reset.
					</div>

					{smsStatus.kind === 'sent' && (
						<div
							className="rounded border border-green-200 bg-green-50 px-3 py-2
								text-xs text-green-800"
						>
							<div className="font-semibold">{smsStatus.deliveryLabel}</div>
							<div className="mt-1">
								To: <span className="font-mono">{smsStatus.to}</span>
							</div>
							<div>
								Sent at: {new Date(smsStatus.sentAt).toLocaleTimeString()}
							</div>
							<div className="mt-1 break-all text-green-600">
								thread_id: {smsStatus.threadId}
							</div>
						</div>
					)}

					{smsStatus.kind === 'skipped' && (
						<div
							className="rounded border border-amber-200 bg-amber-50 px-3 py-2
								text-xs text-amber-800"
						>
							No SMS sent — no pending artifacts or insights to report.
						</div>
					)}

					{smsStatus.kind === 'error' && (
						<div
							className="rounded border border-red-200 bg-red-50 px-3 py-2
								text-xs text-red-800"
						>
							<div className="font-semibold">Failed</div>
							<div className="mt-1 wrap-break-word">{smsStatus.message}</div>
							{smsStatus.threadId && (
								<div className="mt-1 break-all text-red-600">
									thread_id: {smsStatus.threadId}
								</div>
							)}
						</div>
					)}
				</section>

				<section className="space-y-3 border-t border-gray-200 pt-4">
					<h3 className="text-sm font-semibold text-gray-700">
						Simulate IG Comment
					</h3>
					<div className="text-xs text-gray-500">
						Runs the real ig_comment_monitor graph for an active comment watcher
						(no Meta webhook needed). Activate a comment watcher first.
					</div>
					<textarea
						value={simulateText}
						onChange={(e) => setSimulateText(e.target.value)}
						rows={2}
						placeholder="Comment text"
						className="w-full rounded border border-gray-300 px-3 py-2 text-sm
							text-gray-900"
					/>
					<button
						type="button"
						disabled={!userId || isSimulating || !simulateText.trim()}
						onClick={() => void onSimulateComment()}
						className="w-full rounded bg-emerald-600 px-4 py-2 text-sm
							text-white transition hover:bg-emerald-700
							disabled:cursor-not-allowed disabled:opacity-50"
					>
						{isSimulating ? 'Simulating comment...' : 'Simulate IG Comment'}
					</button>

					{simulateStatus.kind === 'ok' && (
						<div
							className="rounded border border-green-200 bg-green-50 px-3 py-2
								text-xs text-green-800"
						>
							<div className="font-semibold">Comment simulated</div>
							<div className="mt-1 break-all">
								comment_id: {simulateStatus.commentId}
							</div>
							<div className="mt-1 text-green-700">
								Matched {simulateStatus.watchersMatched} watcher(s). A reply
								review thread should appear shortly.
							</div>
						</div>
					)}

					{simulateStatus.kind === 'no_watchers' && (
						<div
							className="rounded border border-amber-200 bg-amber-50 px-3 py-2
								text-xs text-amber-800"
						>
							No active comment watcher matched. Create and activate an IG
							comment watcher first.
						</div>
					)}

					{simulateStatus.kind === 'error' && (
						<div
							className="rounded border border-red-200 bg-red-50 px-3 py-2
								text-xs text-red-800"
						>
							<div className="font-semibold">Failed to simulate comment</div>
							<div className="mt-1 wrap-break-word">
								{simulateStatus.message}
							</div>
						</div>
					)}
				</section>

				<section className="space-y-3 border-t border-gray-200 pt-4">
					<h3 className="text-sm font-semibold text-gray-700">Square</h3>
					<div className="flex flex-col gap-2">
						<div className="flex items-center gap-2">
							<Switch
								isSelected={sandboxMode}
								onChange={handleSandboxToggle}
								isEmphasized
								size="L"
							>
								Sandbox Mode
							</Switch>
							<span
								className={`rounded px-2 py-0.5 text-xs font-medium ${
									sandboxMode
										? 'bg-yellow-100 text-yellow-800'
										: 'bg-green-100 text-green-800'
									}`}
							>
								{sandboxMode ? 'Sandbox' : 'Production'}
							</span>
						</div>
					</div>
				</section>

				<section className="space-y-3 border-t border-gray-200 pt-4">
					<h3 className="text-sm font-semibold text-gray-700">Region Block</h3>
					<Switch
						isSelected={forceRegionBlock}
						onChange={handleRegionBlockToggle}
						isEmphasized
						size="L"
					>
						Show region-block interstitial on next login
					</Switch>
					<div
						className="rounded border border-blue-200 bg-blue-50 px-3 py-2
							text-xs text-blue-800"
					>
						While on, the next sign-in or page refresh treats your account as
						one from a blocked region (FR) and shows the &quot;not available in
						your region&quot; page. One-shot: the toggle clears itself when it
						fires — refresh (or sign out and back in) to return to normal.
					</div>
				</section>

				<section className="space-y-3 border-t border-gray-200 pt-4">
					<h3 className="text-sm font-semibold text-gray-700">Empty State</h3>
					<button
						type="button"
						disabled={!userId}
						title="Marks onboarding done and clears all chat threads, recreating the exact state right after onboarding so the first suggestion pill pulses blue when you open Chat."
						onClick={() => {
							if (
								!confirm(
									'Recreate the post-onboarding empty state? This clears all chat ' +
										'threads for this user so the blue first-pill hint shows when ' +
										'you open Chat.',
								)
							) {
								return;
							}
							bridgeApi.sendAction({
								type: 'do_set_onboarding_step',
								phase: 'done',
							});
							bridgeApi.sendAction({ type: 'do_clear_all_chats' });
						}}
						className="w-full rounded bg-blue-600 px-4 py-2 text-sm text-white
							transition hover:bg-blue-700 disabled:cursor-not-allowed
							disabled:opacity-50"
					>
						Show First-Pill Hint (post-onboarding)
					</button>
					<div
						className="rounded border border-blue-200 bg-blue-50 px-3 py-2
							text-xs text-blue-800"
					>
						After clicking, open the <span className="font-semibold">Chat</span>{' '}
						space — the first suggestion pill will pulse blue.
					</div>
				</section>

				<section className="space-y-3 border-t border-gray-200 pt-4">
					<h3 className="text-sm font-semibold text-gray-700">Cleanup</h3>
					<button
						type="button"
						title="Deletes every process (campaigns + jobs and their child artifacts/actions) and every chat thread for this user. Business profile, onboarding, settings, and insights are kept."
						onClick={() => {
							if (
								confirm(
									'Reset workspace? This deletes ALL processes (campaigns + jobs) and ALL chat threads. Business profile and onboarding are kept. This cannot be undone.',
								)
							) {
								bridgeApi.sendAction({ type: 'do_reset_workspace' });
							}
						}}
						className="w-full rounded bg-slate-700 px-4 py-2 text-sm text-white
							transition hover:bg-slate-800"
					>
						Reset Workspace (processes + chats)
					</button>

					<button
						type="button"
						title="Deletes every insight and curated insight for this user from Cosmos. Other state is untouched."
						onClick={() => {
							if (
								confirm(
									'Delete all insights for this user? This cannot be undone.',
								)
							) {
								bridgeApi.sendAction({ type: 'do_clear_all_insights' });
							}
						}}
						className="w-full rounded bg-slate-700 px-4 py-2 text-sm text-white
							transition hover:bg-slate-800"
					>
						Clear All Insights
					</button>

					<button
						type="button"
						title="Deletes today's daily SMS send idempotency row so the next send goes through. Computed against the user's local timezone."
						onClick={() => {
							if (
								confirm(
									"Clear today's daily SMS send record? The next Send Daily SMS click will go through.",
								)
							) {
								bridgeApi.sendAction({
									type: 'do_clear_today_daily_sms_send',
								});
							}
						}}
						className="w-full rounded bg-slate-700 px-4 py-2 text-sm text-white
							transition hover:bg-slate-800"
					>
						Clear Today&apos;s Daily SMS Send Record
					</button>
				</section>
			</div>
		</div>
	);
};
